import { Injectable, Injector } from '@angular/core';
import { AsyncValidatorFn, ValidatorFn } from '@angular/forms';
import { FormInputField, isFormInputField } from '../model/field-declaration';

import { FieldDefinition } from '../model/form.types';
import {
  ConditionalValidatorFn,
  IValidationService,
  PrepareFieldValidationOptions,
  ValidatorConfig,
} from './validation.types';
import { DECLARATIVE_FORMS_VALIDATOR_TOKEN } from './validator.token';

interface BuildValidationResult {
  validators: (ValidatorFn | ConditionalValidatorFn)[];
  asyncValidators: AsyncValidatorFn[];
}

/**
 * Service to provide validation services.
 *
 * It will use the providers injected using the
 * `DECLARATIVE_FORMS_VALIDATOR_TOKEN` token.
 */
@Injectable()
export class ValidationBuilder {
  private _chain?: IValidationService[];

  /**
   * Validation builder services
   */
  private get services(): IValidationService[] {
    if (!this._chain) {
      // Initialize chain from injector
      this._chain = this.injector.get(DECLARATIVE_FORMS_VALIDATOR_TOKEN, []);
    }
    return this._chain;
  }

  constructor(private injector: Injector) {}

  /**
   * @param passedField The field for which validators should be build
   * @param allFields Dictionary of all fields for the form
   *
   * @returns
   * Validator functions
   */
  public buildValidations(
    options: PrepareFieldValidationOptions,
  ): BuildValidationResult {
    // For now, we need to transform the validation
    const field = this.prepareField(options) as FormInputField;

    if (!isFormInputField(field) || !field.widgetOptions.validators) {
      return {
        validators: [],
        asyncValidators: [],
      };
    }

    const fieldValidators = field.widgetOptions.validators;
    if (!fieldValidators) {
      return { validators: [], asyncValidators: [] };
    }

    const syncValidators = fieldValidators.reduce<ValidatorFn[]>(
      (allValidators, v) => [
        ...allValidators,
        ...this.buildValidatorsFromServices(v, field),
      ],
      [],
    );

    const asyncValidators = fieldValidators.reduce<AsyncValidatorFn[]>(
      (allValidators, v) => [
        ...allValidators,
        ...this.buildAsyncValidatorsFromServices(v, field),
      ],
      [],
    );

    return {
      validators: syncValidators,
      asyncValidators,
    };
  }

  /**
   * @param validator The validator configuration
   *
   * @returns
   * An array of validator functions for the given validator configuration
   */
  private buildValidatorsFromServices(
    validator: ValidatorConfig<any>,
    field: FormInputField,
  ): ValidatorFn[] {
    return this.services.reduce<ValidatorFn[]>(
      (validators, s) => [...validators, ...s.buildValidator(validator, field)],
      [],
    );
  }

  /**
   * @param validator The validator configuration
   *
   * @returns
   * An array of validator functions for the given validator configuration
   */
  private buildAsyncValidatorsFromServices(
    validator: ValidatorConfig,
    field: FormInputField,
  ) {
    return this.services.reduce<AsyncValidatorFn[]>(
      (validators, s) =>
        s.buildAsyncValidator
          ? [...validators, ...s.buildAsyncValidator(validator, field)]
          : validators,
      [],
    );
  }

  /**
   * Let services alter field configuration if needed
   *
   * @param options The current field
   */
  private prepareField(
    options: PrepareFieldValidationOptions,
  ): FieldDefinition {
    const { declaration } = options;

    return this.services.reduce(
      (field, service) =>
        service.prepareField?.({ declaration, field }) ?? field,
      options.field,
    );
  }
}
